import type { MediaItem } from "../api/types";
import { commentsLabel, formatDay, truncate } from "./formatters";

/**
 * The account's posts: how they are classified, filtered and painted.
 *
 * The one thing that tells a reel from a story or a carousel lives here, so that question
 * is answered in one place and not on every screen that needs it.
 */

export type MediaCategory = "reels" | "photos" | "stories";

export const TAB_ITEMS: { value: "all" | MediaCategory; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "reels", label: "Reels" },
  { value: "photos", label: "Fotos" },
  { value: "stories", label: "Historias" },
];

const PLACEHOLDER_GRADIENTS = [
  "linear-gradient(135deg,#C9A882,#E6DAC7)",
  "linear-gradient(135deg,#A97E55,#D8C7AC)",
  "linear-gradient(135deg,#6F4E37,#C9A882)",
  "linear-gradient(135deg,#E4A985,#F1E9DC)",
  "linear-gradient(135deg,#B8822A,#E6DAC7)",
  "linear-gradient(135deg,#5E6B6A,#D8C7AC)",
];

/** How much of a caption fits on a card. Shared with the rule cards. */
export const CAPTION_LIMIT_CARD = 95;

const CAPTION_LIMIT_OPTION = 52;

export function isStoryMedia(media: MediaItem | null | undefined): boolean {
  return media?.mediaProductType === "STORY" || media?.mediaType === "STORY";
}

export function isReelMedia(media: MediaItem | null | undefined): boolean {
  return media?.mediaProductType === "REELS";
}

/** Category used by the tab filter. */
export function mediaCategory(media: MediaItem): MediaCategory {
  if (isReelMedia(media)) return "reels";
  if (isStoryMedia(media)) return "stories";
  return "photos";
}

function mediaKindLabel(media: MediaItem): string {
  if (isStoryMedia(media)) return "HISTORIA";
  if (isReelMedia(media)) return "REEL";
  return media.mediaType === "CAROUSEL_ALBUM" ? "CARRUSEL" : "FOTO";
}

export type PostView = ReturnType<typeof toPostView>;

export function toPostView(media: MediaItem, index: number, onAutomate?: (media: MediaItem) => void) {
  const image = media.thumbnailUrl || media.mediaUrl;
  return {
    ...media,
    grad: PLACEHOLDER_GRADIENTS[index % PLACEHOLDER_GRADIENTS.length],
    commentsLabel: commentsLabel(media.commentsCount),
    date: formatDay(media.timestamp),
    kind: mediaKindLabel(media),
    type: mediaCategory(media),
    image,
    imageAlt: media.caption
      ? `Vista previa: ${truncate(media.caption, CAPTION_LIMIT_OPTION)}`
      : `Vista previa de ${mediaKindLabel(media).toLowerCase()}`,
    noImage: !image,
    onAutomate: () => onAutomate?.(media),
  };
}

export function filterPosts<T extends { type: string; caption?: string | null }>(
  posts: T[],
  { query = "", tab = "all" }: { query?: string; tab?: string } = {},
): T[] {
  const needle = query.trim().toLowerCase();
  return posts.filter(
    (post) =>
      (tab === "all" || post.type === tab) &&
      (!needle ||
        String(post.caption ?? "")
          .toLowerCase()
          .includes(needle)),
  );
}

export function toPostOptions(media: MediaItem[]) {
  return media.map((item) => ({ id: item.id, short: truncate(item.caption, CAPTION_LIMIT_OPTION) }));
}

/** Index by id: it avoids walking every post for every rule. */
export function indexMediaById(media: MediaItem[]): Map<string, MediaItem> {
  return new Map(media.map((item) => [item.id, item]));
}
